import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Activity,
  Brain,
  CheckCircle2,
  Cpu,
  Sparkles,
  Users,
} from 'lucide-react'
import { Breadcrumb } from '../../components/Breadcrumb'
import { api, extractApiError } from '../../lib/api'
import { platformAiUsageResponse, plansResponse } from '../../lib/types'
import { DataTable } from '../../components/table/DataTable'
import { CountryFilter } from '../../components/ui/CountryFilter'
import { CountryCell } from '../../components/ui/CountryCell'
import { StatusBadge } from '../../components/ui/StatusBadge'
import { FilterRow } from '../../components/filters/FilterRow'
import { formatDateTime, formatNumber } from '../../lib/format'
import { useChartHover } from '../../components/charts/use-chart-hover'
import { ChartTooltip } from '../../components/charts/ChartTooltip'
import { usePageSize } from '../../lib/display-prefs'

const WINDOW_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 365, label: 'Last 365 days' },
]

function fmtNumber(n) {
  return formatNumber(Number(n ?? 0))
}
function fmtCompactTokens(n) {
  // 1.2M / 12.3k / 850 — keeps the column narrow while still readable.
  const v = Number(n ?? 0)
  if (v >= 1_000_000) return `${formatNumber(v / 1_000_000, { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: false })}M`
  if (v >= 1_000) return `${formatNumber(v / 1_000, { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: false })}k`
  return String(v)
}
function fmtRelative(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  const diffMs = Date.now() - d.getTime()
  const diffMin = Math.round(diffMs / 60_000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60)
    return `${diffMin} ${diffMin === 1 ? 'minute' : 'minutes'} ago`
  const diffH = Math.round(diffMin / 60)
  if (diffH < 24) return `${diffH} ${diffH === 1 ? 'hour' : 'hours'} ago`
  const diffD = Math.round(diffH / 24)
  if (diffD < 7) return `${diffD} ${diffD === 1 ? 'day' : 'days'} ago`
  // > 7 days falls back to the platform-wide DD/MM/YYYY HH:MM AM/PM
  // format so older entries read the same as every other date on screen.
  return formatDateTime(iso)
}

/** Platform Admin → AI Usage. Cross-tenant rollup of LLM events for a
 *  configurable window. Built to spot heavy users and capacity
 *  pressure; the per-tenant Company Admin view stays for tenants to
 *  see their own usage. */
export function PlatformAiUsagePage() {
  const [days, setDays] = useState(30)
  const [search, setSearch] = useState('')
  const [planFilter, setPlanFilter] = useState('all')
  // Status + country filters mirror the Companies-page vocabulary so
  // the platform-admin pages share one filter UX.
  const [statusFilter, setStatusFilter] = useState('all')
  const [countryFilter, setCountryFilter] = useState('all')
  const [page, setPage] = useState(1)
  // Starts at (and follows) the account rows-per-page (My Preferences);
  // this list's API accepts every standard size up to 500.
  const [limit, setLimit] = usePageSize()

  // A filter is "active" when it differs from its default. Drives the
  // Clear-all-filters button, which only appears once at least one
  // filter is narrowing the table. The Window select defaults to 30
  // days, so a non-30 window counts as an active filter too.
  const hasAnyFilter =
    search.trim() !== '' ||
    planFilter !== 'all' ||
    statusFilter !== 'all' ||
    countryFilter !== 'all' ||
    days !== 30

  function clearFilters() {
    setSearch('')
    setPlanFilter('all')
    setStatusFilter('all')
    setCountryFilter('all')
    setDays(30)
    setPage(1)
  }

  // Shared server params (window + Search + Status + Country + Plan) for
  // both the paged query and the export "all records" fetch.
  function filterParams() {
    const params = { days }
    if (search.trim()) params.search = search.trim()
    if (planFilter !== 'all') params.plan = planFilter
    if (statusFilter !== 'all') params.status = statusFilter
    if (countryFilter !== 'all') params.country = countryFilter
    return params
  }

  const usageQuery = useQuery({
    queryKey: [
      'platform-ai-usage',
      days,
      search,
      planFilter,
      statusFilter,
      countryFilter,
      page,
      limit,
    ],
    queryFn: async () => {
      const res = await api.get('/super-admin/platform/ai-usage', {
        params: { ...filterParams(), page, limit },
      })
      return platformAiUsageResponse.parse(res.data)
    },
    placeholderData: (prev) => prev,
  })

  // Plan catalogue drives the filter dropdown + the row pill label.
  // Used to be a hardcoded two-entry map (flowdesk_only / plus_sop)
  // that silently dropped custom plans from the filter.
  const plansQuery = useQuery({
    queryKey: ['platform-plans-dropdown'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await api.get('/super-admin/plans')
      return plansResponse.parse(res.data)
    },
  })
  const planOptions = useMemo(() => {
    const list = plansQuery.data?.data ?? []
    return [...list]
      // Hide inactive ("Hidden"/draft) plans from the filter.
      .filter((p) => p.isActive)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
      .map((p) => ({ value: p.code, label: p.name }))
  }, [plansQuery.data])
  const planNameByCode = useMemo(() => {
    const map = new Map()
    for (const p of plansQuery.data?.data ?? []) map.set(p.code, p.name)
    return map
  }, [plansQuery.data])

  const rows = usageQuery.data?.data ?? []
  const totals = usageQuery.data?.totals
  const pageInfo = usageQuery.data?.pagination ?? null

  // Export pages through the filtered feed (bounded); DataTable sorts the
  // current page internally via each column's `sortAccessor`.
  async function fetchAllForExport() {
    const all = []
    for (let p = 1; p <= 50; p += 1) {
      const res = await api.get('/super-admin/platform/ai-usage', {
        params: { ...filterParams(), page: p, limit: 500 },
      })
      const parsed = platformAiUsageResponse.parse(res.data)
      all.push(...(parsed.data ?? []))
      const info = parsed.pagination
      if (!info || p >= info.totalPages) break
    }
    return all
  }

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Company',
        grow: true,
        pinnable: true,
        minWidth: 200,
        sortAccessor: (r) => (r.name ?? '').toLowerCase(),
        exportValue: (r) => r.name ?? '',
        render: (r) => (
          <p className="font-semibold text-[color:var(--color-ink-strong)] truncate">
            {r.name}
          </p>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        minWidth: 110,
        sortAccessor: (r) => r.status ?? '',
        exportValue: (r) => r.status ?? '',
        render: (r) => <StatusBadge status={r.status} />,
      },
      {
        key: 'plan',
        header: 'Plan',
        minWidth: 130,
        sortAccessor: (r) =>
          (planNameByCode.get(r.planTier) ?? r.planTier ?? '').toLowerCase(),
        exportValue: (r) =>
          r.planTier?.trim() ? (planNameByCode.get(r.planTier) ?? r.planTier) : 'Awaiting plan',
        render: (r) => (
          <PlanPill planTier={r.planTier} planNameByCode={planNameByCode} />
        ),
      },
      {
        key: 'country',
        header: 'Country',
        minWidth: 120,
        sortAccessor: (r) => (r.country ?? '').toLowerCase(),
        exportValue: (r) => r.country ?? '',
        render: (r) => (
          <CountryCell country={r.country} billingCurrency={r.billingCurrency} />
        ),
      },
      {
        key: 'callCount',
        header: 'Calls',
        minWidth: 90,
        sortAccessor: (r) => Number(r.callCount ?? 0),
        exportValue: (r) => Number(r.callCount ?? 0),
        cellClassName: 'text-[color:var(--color-ink-strong)] tabular-nums',
        render: (r) => fmtNumber(r.callCount),
      },
      {
        key: 'successCount',
        header: 'Success',
        minWidth: 90,
        sortAccessor: (r) => Number(r.successCount ?? 0),
        exportValue: (r) => Number(r.successCount ?? 0),
        cellClassName: 'text-[color:var(--color-ink-muted)] tabular-nums',
        render: (r) => fmtNumber(r.successCount),
      },
      {
        key: 'estTokens',
        header: 'Estimated Tokens',
        minWidth: 140,
        sortAccessor: (r) => Number(r.estTokens ?? 0),
        exportValue: (r) => Number(r.estTokens ?? 0),
        cellClassName: 'text-[color:var(--color-ink-strong)] tabular-nums',
        render: (r) => (
          <span title={`${fmtNumber(r.estTokens)} tokens`}>
            {fmtCompactTokens(r.estTokens)}
          </span>
        ),
      },
      {
        key: 'quotaPct',
        header: 'Quota Usage',
        minWidth: 160,
        sortAccessor: (r) =>
          r.quotaPct == null ? Number.NEGATIVE_INFINITY : Number(r.quotaPct),
        exportValue: (r) => (r.quotaPct == null ? '' : `${r.quotaPct}%`),
        render: (r) => (
          <QuotaBar
            pct={r.quotaPct}
            quota={r.monthlyAiQuotaTokens}
            planTier={r.planTier}
          />
        ),
      },
      {
        key: 'lastCallAt',
        header: 'Last Call',
        minWidth: 150,
        sortAccessor: (r) => (r.lastCallAt ? new Date(r.lastCallAt) : null),
        exportValue: (r) => (r.lastCallAt ? formatDateTime(r.lastCallAt) : ''),
        cellClassName:
          'text-xs text-[color:var(--color-ink-muted)] whitespace-nowrap',
        render: (r) => fmtRelative(r.lastCallAt),
      },
    ],
    [planNameByCode],
  )

  return (
    <div>
      <Breadcrumb current="AI Usage" homePath="/super-admin/dashboard" />

      <div className="mb-5">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
          <Brain
            size={20}
            className="text-[color:var(--color-accent-strong)]"
          />
          AI Usage
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
          See how much AI each company is using.
        </p>
      </div>

      {/* Totals strip — only meaningful once data has loaded. Same
       *  `KpiCard` chrome as the Companies / Plans / All Users
       *  pages so the four platform-admin list surfaces share one
       *  KPI vocabulary. */}
      {totals ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <KpiCard
            label="AI Active"
            value={`${fmtNumber(totals.activeTenants)} / ${fmtNumber(totals.tenantCount)}`}
            icon={Users}
            tone="violet"
            hint={`Made ≥1 AI call in last ${usageQuery.data?.range.days} days`}
          />
          <KpiCard
            label="Total AI Calls"
            value={fmtNumber(totals.callCount)}
            icon={Activity}
            tone="sky"
            hint={`Across ${fmtNumber(totals.tenantCount)} ${totals.tenantCount === 1 ? 'company' : 'companies'} in last ${usageQuery.data?.range.days} days`}
          />
          <KpiCard
            label="Successful"
            value={fmtNumber(totals.successCount)}
            icon={CheckCircle2}
            tone="emerald"
            hint={
              totals.callCount > 0
                ? `${Math.round((totals.successCount / totals.callCount) * 100)}% success rate`
                : 'No calls in window'
            }
          />
          <KpiCard
            label="Est. Tokens"
            value={fmtNumber(totals.estTokens)}
            icon={Cpu}
            tone="amber"
            hint={`${fmtNumber(totals.inputChars + totals.outputChars)} chars`}
          />
        </div>
      ) : null}

      {/* Filter bar — shared FilterRow primitive. CountryFilter keeps
       *  its custom popover and sits as a sibling. The AI-active toggle
       *  is a checkbox specific to this page; rendered inside the row
       *  so it shares the spacing rhythm. */}
      <FilterRow>
        <FilterRow.Search
          value={search}
          onChange={(v) => {
            setSearch(v)
            setPage(1)
          }}
          placeholder="Search by Company Name"
        />
        <FilterRow.Select
          ariaLabel="Status"
          placeholder="All statuses"
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v)
            setPage(1)
          }}
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'active', label: 'Active' },
            { value: 'suspended', label: 'Suspended' },
            { value: 'offboarded', label: 'Offboarded' },
          ]}
        />
        <CountryFilter
          value={countryFilter}
          onChange={(next) => {
            setCountryFilter(next)
            setPage(1)
          }}
        />
        <FilterRow.Select
          ariaLabel="Plan"
          placeholder="All plans"
          value={planFilter}
          onChange={(v) => {
            setPlanFilter(v)
            setPage(1)
          }}
          options={[
            { value: 'all', label: 'All plans' },
            ...planOptions,
          ]}
        />
        <FilterRow.Select
          ariaLabel="Window"
          value={String(days)}
          onChange={(v) => {
            setDays(Number(v))
            setPage(1)
          }}
          options={WINDOW_OPTIONS.map((o) => ({
            value: String(o.value),
            label: o.label,
          }))}
        />
        <FilterRow.Spacer />
        <FilterRow.Clear show={hasAnyFilter} onClick={clearFilters} />
        {/* Manual refresh — fires the same query React Query would fire
         *  on window-focus, but on demand. Icon animates while the
         *  request is in flight so a click always gives feedback (bug
         *  0013274 was the sibling Storage page's refresh; wiring the
         *  same primitive here so both stay consistent). */}
        <FilterRow.Refresh
          onClick={() => usageQuery.refetch()}
          loading={usageQuery.isFetching}
        />
      </FilterRow>

      {/* Server-paginated per-tenant AI usage on the shared DataTable.
       *  Search + Status + Country + Plan + Window filters and the KPI
       *  strip above stay page-owned (server params + server `totals`), so
       *  DataTable's own search is suppressed. Read-only — no row actions;
       *  sort reorders the current page. */}
      <DataTable
        tableId="platform-ai-usage"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        loading={usageQuery.isLoading}
        fetching={usageQuery.isFetching}
        error={
          usageQuery.isError ? extractApiError(usageQuery.error).message : null
        }
        onRetry={() => usageQuery.refetch()}
        searchable={false}
        defaultSort={[{ key: 'callCount', dir: 'desc' }]}
        page={pageInfo?.page ?? page}
        totalPages={pageInfo?.totalPages ?? 1}
        total={pageInfo?.total ?? 0}
        limit={pageInfo?.limit ?? limit}
        onPageChange={setPage}
        onLimitChange={(n) => {
          setLimit(n)
          setPage(1)
        }}
        exportBaseName="ai-usage"
        exportTitle="AI Usage"
        getExportRows={fetchAllForExport}
        emptyState={{
          icon: Sparkles,
          title: 'No AI activity matches your filters',
        }}
      />
    </div>
  )
}

/** Per-tone tile + halo bleed for the KPI strip. Same palette as
 *  the Companies / Plans / All Users pages so the four platform-
 *  admin surfaces share one visual vocabulary. */
const KPI_TONES = {
  violet: {
    tile: 'bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10',
    halo: 'from-[#EDE9FE] to-transparent dark:from-transparent',
  },
  emerald: {
    tile: 'bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10',
    halo: 'from-[#D1FAE5] to-transparent dark:from-transparent',
  },
  amber: {
    tile: 'bg-[#FFEDD5] text-[#EA580C] border border-[#FED7AA] dark:bg-white/[0.06] dark:text-orange-300 dark:border-white/10',
    halo: 'from-[#FFEDD5] to-transparent dark:from-transparent',
  },
  sky: {
    tile: 'bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE] dark:bg-white/[0.06] dark:text-sky-300 dark:border-white/10',
    halo: 'from-[#DBEAFE] to-transparent dark:from-transparent',
  },
  rose: {
    tile: 'bg-[#FFE4E6] text-[#E11D48] border border-[#FECDD3] dark:bg-white/[0.06] dark:text-rose-300 dark:border-white/10',
    halo: 'from-[#FFE4E6] to-transparent dark:from-transparent',
  },
}

/** Single-stat KPI card matching the Companies-page `KpiCard` shape
 *  beat-for-beat — rounded-xl chrome, blurred halo bleed top-right,
 *  12×12 tinted icon chip, 4xl font-display value, uppercase
 *  tracking-wider label, optional muted hint underneath. */
function KpiCard({ label, value, icon: Icon, tone = 'violet', hint }) {
  const tones = KPI_TONES[tone] ?? KPI_TONES.violet
  return (
    <div className="flex flex-col relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 min-h-[120px]">
      <div
        aria-hidden="true"
        className={
          'pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full blur-2xl opacity-70 bg-gradient-to-br ' +
          tones.halo
        }
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-sm uppercase tracking-wider text-[color:var(--color-ink-muted)] font-medium m-0">
            {label}
          </h3>
          <p className="mt-2 font-display text-3xl text-[color:var(--color-ink-strong)] tabular-nums leading-none">
            {typeof value === 'number'
              ? formatNumber(value)
              : value}
          </p>
          {hint ? (
            <p className="mt-2 text-[13px] text-[color:var(--color-ink-muted)]">
              {hint}
            </p>
          ) : null}
        </div>
        <div
          className={
            'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ' +
            tones.tile
          }
        >
          <Icon size={24} />
        </div>
      </div>
    </div>
  )
}

function PlanPill({ planTier, planNameByCode }) {
  // A no-plan company (planTier '' or null — the app-wide "No plan" sentinel)
  // shows the canonical amber "Awaiting plan" badge, matching the Companies page
  // (TenantsPage), instead of a blank accent pill.
  const code = typeof planTier === 'string' ? planTier.trim() : planTier
  if (!code) {
    return (
      <span
        className="inline-flex text-xs uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]"
        title="Awaiting plan selection — this self-signup company hasn't chosen a plan yet and is routed to Billing to pick one."
      >
        Awaiting plan
      </span>
    )
  }
  return (
    <span className="inline-flex text-xs uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]">
      {planNameByCode?.get(code) ?? code}
    </span>
  )
}

/** Inline usage bar — blue under 75%, amber 75-100%, red over.
 *  Quota=null on the server comes from TWO different states: (a) an
 *  enterprise / unlimited plan (legitimately no cap), and (b) a tenant
 *  with no plan assigned (nothing to derive a cap from). We split them
 *  by inspecting `planTier` — an empty string is the app-wide "No plan"
 *  sentinel (types.js). Rendering "Unlimited" for a no-plan tenant read
 *  as "granted infinite resources" (QA bug 0016514); "No plan" makes
 *  it clear the row is uncapped because it is unbilled, not because
 *  it is entitled. */
function QuotaBar({ pct, quota, planTier }) {
  const { ref, hover, track, clear } = useChartHover()
  const hasPlan = typeof planTier === 'string' && planTier.trim() !== ''
  if (quota === null || quota === undefined) {
    if (!hasPlan) {
      return (
        <span className="text-sm text-[color:var(--color-ink-muted)] italic">
          No plan
        </span>
      )
    }
    return (
      <span className="text-sm text-[color:var(--color-ink-muted)] italic">
        Unlimited
      </span>
    )
  }
  const value = pct ?? 0
  const tone =
    value >= 100
      ? 'bg-[color:var(--color-priority-urgent)]'
      : value >= 75
        ? 'bg-[color:var(--color-warning)]'
        : 'bg-[color:var(--color-primary)]'
  // Mirror the bar's green/amber/red fill into the tooltip's colour dot.
  const dotColor =
    value >= 100
      ? 'var(--color-priority-urgent)'
      : value >= 75
        ? 'var(--color-warning)'
        : 'var(--color-primary)'
  const payload = {
    color: dotColor,
    label: 'AI usage',
    value: `${Math.round(value)}% used`,
    hint: `${fmtNumber(quota)} token quota`,
  }
  return (
    <div
      ref={ref}
      className="relative flex items-center gap-2"
      onMouseEnter={track(payload)}
      onMouseMove={track(payload)}
      onMouseLeave={clear}
    >
      {/* Track uses `--color-rule` instead of `--color-canvas` —
       *  the canvas tone is identical to the table's row-hover
       *  background, so on hover the empty track blended into the
       *  row and the whole bar visually disappeared (especially at
       *  0% fill). `--color-rule` is the standard border tone and
       *  stays visible against every row state. */}
      <div className="flex-1 h-1.5 rounded-full bg-[color:var(--color-rule)] overflow-hidden">
        <div
          className={'h-full rounded-full transition-[width] duration-500 ' + tone}
          // 2% min-width keeps a tiny-but-non-zero usage visible, but
          // when the company has logged literally zero usage we want
          // the bar empty (no sliver). Falls back to 0% in that case.
          style={{ width: `${value > 0 ? Math.min(100, Math.max(2, value)) : 0}%` }}
        />
      </div>
      <span className="text-xs text-[color:var(--color-ink-muted)] tabular-nums shrink-0 w-10 text-right">
        {value}%
      </span>
      {hover ? <ChartTooltip {...hover} /> : null}
    </div>
  )
}

